import apiService from '../APIService';
import {
  fixtureDelay,
  fixtureHealthSystems,
  fixtureJobSummary,
  fixturePurposeOfUses,
  fixtureRequests,
  fixtureStatusCounts,
  fixtureSummaryRows,
} from './fixtures';
import { USE_FIXTURES } from '../devFlags';
import { ENDPOINTS } from '../constants';
import type {
  APIResponse,
  HealthSystemListSuccess,
  JobSummarySuccess,
  PurposeOfUseSuccess,
  SignedUrlSuccess,
  SummaryListSuccess,
} from '../interfaces/APIResponse';
import type {
  FilterField,
  HealthSystemOption,
  HealthSystemSummaryRow,
  Option,
  PurposeOfUseOption,
  RequestCountResponse,
  RequestListResponse,
  RequestRow,
  StatusCount,
} from '../interfaces/Dashboard';

/**
 * All AR dashboard reads. Every function resolves to an empty/neutral value on
 * failure rather than throwing, so a dead endpoint renders "No Record Found"
 * instead of blanking the page (the behaviour the legacy app relied on).
 */

interface RequestCountSuccess {
  requestCountList?: unknown[];
  healthSystemwiseRequestCountList?: unknown[];
  healthSystemwiseSummaryList?: HealthSystemSummaryRow[];
}

export interface FetchRequestCountParams {
  fromdate: string | null;
  todate: string | null;
  filterType: string;
  isFilterByCreatedDate: boolean;
  purposeOfUse?: string;
}

/** Totals + per-health-system breakdown that drive the summary charts/grids. */
export const fetchAllRequestCount = async ({
  fromdate,
  todate,
  filterType,
  isFilterByCreatedDate,
  purposeOfUse,
}: FetchRequestCountParams): Promise<RequestCountResponse> => {
  if (USE_FIXTURES) {
    await fixtureDelay();
    return {
      requestCountList: [],
      healthSystemwiseRequestCountList: [],
      healthSystemwiseSummaryList: fixtureSummaryRows(),
    };
  }

  const params: Record<string, unknown> = {
    filtertype: filterType,
    isFilterByCreatedDate,
  };

  if (fromdate) params.fromdate = fromdate;
  if (todate) params.todate = todate;
  // "All" is the UI's no-filter sentinel and must not reach the API.
  if (purposeOfUse && purposeOfUse !== 'All')
    params.purposeofuse = purposeOfUse;

  const response = await apiService.get<APIResponse<RequestCountSuccess>>(
    ENDPOINTS.FETCH_ALL_REQUEST_COUNT,
    params,
  );

  const success = response?.data?.success;
  return {
    requestCountList: success?.requestCountList ?? [],
    healthSystemwiseRequestCountList:
      success?.healthSystemwiseRequestCountList ?? [],
    healthSystemwiseSummaryList: success?.healthSystemwiseSummaryList ?? [],
  };
};

/** Health systems for the Dashboard selector, shaped as Autocomplete options. */
export const fetchAllHealthSystems = async (): Promise<
  HealthSystemOption[]
> => {
  if (USE_FIXTURES) {
    await fixtureDelay();
    return fixtureHealthSystems();
  }

  const response = await apiService.get<APIResponse<HealthSystemListSuccess>>(
    ENDPOINTS.FETCH_ALL_HEALTH_SYSTEMS,
  );

  const list = response?.data?.success?.healthSystemList ?? [];
  return list.map((entry) => ({
    ...entry,
    value: entry.facilityid,
    label: `${entry.name} - ${entry.facilityid}`,
  }));
};

/** Per-status counts for one health system — the Dashboard donut chart. */
export const fetchHealthSystemSummaryCount = async (
  vendorid: string | null,
): Promise<StatusCount[]> => {
  if (USE_FIXTURES) {
    await fixtureDelay();
    return fixtureStatusCounts();
  }

  const response = await apiService.get<APIResponse<SummaryListSuccess>>(
    ENDPOINTS.FETCH_HEALTH_SYSTEM_SUMMARY_COUNT,
    { vendorid },
  );

  return response?.data?.success?.summaryList ?? [];
};

/**
 * Flattens the Dashboard search bar into the request body. Only non-empty
 * fields are sent; `purposeofuse: "All"` is treated as "no filter".
 */
const buildRequestFilters = (
  searchFields: FilterField[] | null,
): Record<string, string> => {
  const filters: Record<string, string> = {};
  if (!searchFields) return filters;

  searchFields.forEach((field) => {
    const { value } = field;
    if (value === null || value === undefined || value === '') return;

    if (field.type === 'select') {
      const selected = value as Option;
      if (!selected?.value) return;
      if (field.responseValue === 'purposeofuse' && selected.value === 'All') {
        return;
      }
      filters[field.responseValue] = selected.value;
    } else {
      filters[field.responseValue] = value as string;
    }
  });

  return filters;
};

export interface FetchAllRequestsParams {
  vendorid: string | null;
  searchFields?: FilterField[] | null;
  page: number;
  rowsPerPage: number;
}

/** Paged request grid for the selected health system. */
export const fetchAllRequests = async ({
  vendorid,
  searchFields = null,
  page,
  rowsPerPage,
}: FetchAllRequestsParams): Promise<RequestListResponse> => {
  if (USE_FIXTURES) {
    await fixtureDelay();
    return fixtureRequests(page, rowsPerPage);
  }

  const response = await apiService.post<
    APIResponse<{ requests?: RequestRow[]; totalRequests?: number }>
  >(ENDPOINTS.FETCH_ALL_REQUESTS, {
    vendorid,
    pagenumber: page,
    pagesize: rowsPerPage,
    ...buildRequestFilters(searchFields),
  });

  const success = response?.data?.success;
  const requests = success?.requests ?? [];

  return {
    requestList: requests.map((request) => ({
      ...request,
      showOutputDetailsLoader: false,
      showJobSummaryLoader: false,
    })),
    totalRequests: success?.totalRequests ?? null,
  };
};

/** Signed URL for the retrieved-document PDF, or `null` if none is available. */
export const downloadRADocumentUsingSignedURL = async (
  requestid: string,
): Promise<string | null> => {
  const response = await apiService.post<APIResponse<SignedUrlSuccess>>(
    ENDPOINTS.DOWNLOAD_PDF_SIGNED_URL,
    { requestid },
  );

  return (
    response?.data?.success?.patientmedicalrecord?.mrdetails?.[0]?.file ?? null
  );
};

/**
 * Error/log detail rows for one request. `requesterrefid` is stamped onto each
 * row from the grid because the endpoint does not return it.
 */
export const fetchJobSummary = async (
  requestid: string,
  requesterrefid: string,
) => {
  if (USE_FIXTURES) {
    await fixtureDelay();
    return fixtureJobSummary(requestid, requesterrefid);
  }

  const response = await apiService.post<APIResponse<JobSummarySuccess>>(
    ENDPOINTS.FETCH_JOB_SUMMARY,
    { requestid },
  );

  const errorDetails = response?.data?.success?.errordetails ?? [];
  return errorDetails.map((detail) => ({ requesterrefid, ...detail }));
};

/** Purpose-of-use options, always led by the "All" sentinel. */
export const fetchAllPurposeOfUses = async (): Promise<
  PurposeOfUseOption[]
> => {
  if (USE_FIXTURES) {
    await fixtureDelay();
    return fixturePurposeOfUses();
  }

  const response = await apiService.get<APIResponse<PurposeOfUseSuccess>>(
    ENDPOINTS.FETCH_ALL_PURPOSE_OF_USES,
  );

  const list = response?.data?.success?.purposeofuse ?? [];
  return [
    { value: 'All', label: 'All' },
    ...list.map((entry) => ({
      ...entry,
      value: entry.documenttypeid,
      label: entry.documenttype,
    })),
  ];
};
